import { useEffect, useMemo, useState } from "react"
import { Eye } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { LifeWheelRadar } from "@/components/planning/LifeWheelRadar"
import {
  AUDIT_CATEGORIES,
  AUDIT_QUESTIONS,
  QUESTIONS_PER_CATEGORY,
} from "@/components/planning/audit-questions"
import { RatingRowDisplay } from "@/components/planning/RatingRow"
import { cn } from "@/lib/utils"

const MAX_TOTAL = AUDIT_CATEGORIES.length * 25

export function formatWheelSnapshotTakenAt(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function formatWheelSnapshotSource(source) {
  switch (source) {
    case "intake":
      return "Initial intake"
    case "standalone_retake":
      return "Retake"
    case "quarter_audit":
      return "Quarter audit"
    case "monthly_seed":
      return "Monthly snapshot"
    default:
      return source
  }
}

function hasFullAuditScores(auditScores) {
  if (!auditScores || typeof auditScores !== "object") return false
  return AUDIT_CATEGORIES.every((c) => {
    const arr = auditScores[c.key]
    return Array.isArray(arr) && arr.length === QUESTIONS_PER_CATEGORY
  })
}

function SnapshotAnswersBody({ snapshot }) {
  if (!hasFullAuditScores(snapshot?.auditScores)) {
    return (
      <p className="text-sm text-zinc-600">
        Individual question responses aren&apos;t available for this snapshot.
      </p>
    )
  }

  return (
    <div className="max-h-[65vh] space-y-6 overflow-y-auto pr-1">
      {AUDIT_CATEGORIES.map((cat) => (
        <section key={cat.key}>
          <h3 className="mb-2 font-display text-base text-zinc-900">{cat.label}</h3>
          <ol className="list-none space-y-3">
            {AUDIT_QUESTIONS[cat.key].map((q, i) => (
              <li
                key={i}
                className="flex flex-col gap-3 border-b border-zinc-100 pb-3 text-sm last:border-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
              >
                <p className="text-zinc-800 sm:flex-1">{q}</p>
                <RatingRowDisplay
                  value={snapshot.auditScores[cat.key][i]}
                  ariaLabel={`${cat.label}: ${q}`}
                />
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}

/**
 * Timeline of WheelSnapshot rows: comparision radar, table, answers dialog.
 * Used on the member dashboard (Life Assessment) and coach member history page.
 */
export function WheelSnapshotHistorySection({ snapshots = [], embedded = false }) {
  const latestId = snapshots.length ? snapshots[snapshots.length - 1].id : null
  const [focusedId, setFocusedId] = useState(latestId)
  const [answersOpen, setAnswersOpen] = useState(false)
  const [answersSnapshot, setAnswersSnapshot] = useState(null)

  useEffect(() => {
    if (latestId == null) return
    const ids = new Set(snapshots.map((s) => s.id))
    if (focusedId == null || !ids.has(focusedId)) {
      setFocusedId(latestId)
    }
  }, [snapshots, focusedId, latestId])

  const focused = useMemo(() => {
    if (!snapshots.length) return null
    return snapshots.find((s) => s.id === focusedId) ?? snapshots[snapshots.length - 1]
  }, [snapshots, focusedId])

  const inner = (
    <>
      {snapshots.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-zinc-500">
            No history yet — complete your first assessment to start your timeline.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span>{formatWheelSnapshotTakenAt(focused.takenAt)}</span>
                <span className="text-base font-normal text-zinc-500">
                  · {formatWheelSnapshotSource(focused.source)}
                </span>
              </CardTitle>
              {focused.wheelTotalScore != null && (
                <p className="text-sm text-zinc-500">
                  {focused.wheelTotalScore}/{MAX_TOTAL} total — select a row or the eye icon to
                  compare
                </p>
              )}
            </CardHeader>
            <CardContent>
              <LifeWheelRadar scores={focused.wheelScores || {}} size={280} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All snapshots</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left">
                    <th className="w-12 py-2 pl-0 pr-1 text-center font-medium text-zinc-700">
                      <span className="sr-only">Show on chart</span>
                    </th>
                    <th className="py-2 pr-3 font-medium text-zinc-700">Date</th>
                    <th className="py-2 pr-3 font-medium text-zinc-700">Source</th>
                    {AUDIT_CATEGORIES.map((cat) => (
                      <th key={cat.key} className="px-2 py-2 text-center font-medium text-zinc-700">
                        {cat.label}
                      </th>
                    ))}
                    <th className="py-2 pl-2 text-center font-medium text-zinc-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((s) => (
                    <tr
                      key={s.id}
                      className={cn(
                        "border-b border-zinc-100 transition-colors hover:bg-zinc-50/90",
                        focusedId === s.id && "bg-kf-red/[0.06]",
                      )}
                    >
                      <td className="py-2 pl-0 pr-1 text-center align-middle">
                        <button
                          type="button"
                          className={cn(
                            "inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-kf-red",
                            focusedId === s.id && "bg-red-50 text-kf-red",
                          )}
                          aria-label={`Show ${formatWheelSnapshotTakenAt(s.takenAt)} on chart`}
                          aria-pressed={focusedId === s.id}
                          onClick={(e) => {
                            e.stopPropagation()
                            setFocusedId(s.id)
                          }}
                        >
                          <Eye className="h-4 w-4" aria-hidden />
                        </button>
                      </td>
                      <td
                        className="cursor-pointer py-2 pr-3 text-zinc-900"
                        onClick={() => {
                          setFocusedId(s.id)
                          setAnswersSnapshot(s)
                          setAnswersOpen(true)
                        }}
                      >
                        {formatWheelSnapshotTakenAt(s.takenAt)}
                      </td>
                      <td
                        className="cursor-pointer py-2 pr-3 text-zinc-500"
                        onClick={() => {
                          setFocusedId(s.id)
                          setAnswersSnapshot(s)
                          setAnswersOpen(true)
                        }}
                      >
                        {formatWheelSnapshotSource(s.source)}
                      </td>
                      {AUDIT_CATEGORIES.map((cat) => (
                        <td
                          key={cat.key}
                          className="cursor-pointer px-2 py-2 text-center text-zinc-700"
                          onClick={() => {
                            setFocusedId(s.id)
                            setAnswersSnapshot(s)
                            setAnswersOpen(true)
                          }}
                        >
                          {s.wheelScores?.[cat.key] ?? "—"}
                        </td>
                      ))}
                      <td
                        className="cursor-pointer py-2 pl-2 text-center font-medium text-zinc-900"
                        onClick={() => {
                          setFocusedId(s.id)
                          setAnswersSnapshot(s)
                          setAnswersOpen(true)
                        }}
                      >
                        {s.wheelTotalScore != null ? `${s.wheelTotalScore}/${MAX_TOTAL}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Dialog
            open={answersOpen}
            onOpenChange={(open) => {
              setAnswersOpen(open)
              if (!open) setAnswersSnapshot(null)
            }}
          >
            <DialogContent className="sm:max-w-2xl" showCloseButton>
              <DialogHeader>
                <DialogTitle>
                  {answersSnapshot
                    ? `${formatWheelSnapshotTakenAt(answersSnapshot.takenAt)} · ${formatWheelSnapshotSource(answersSnapshot.source)}`
                    : "Snapshot"}
                </DialogTitle>
                <DialogDescription>
                  All thirty Integrated Life Audit questions and the score chosen for each (1–5).
                </DialogDescription>
              </DialogHeader>
              {answersSnapshot && <SnapshotAnswersBody snapshot={answersSnapshot} />}
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  )

  if (embedded) {
    return (
      <section
        id="wheel-snapshots"
        className="scroll-mt-6 space-y-4"
        aria-labelledby="wheel-snapshots-heading"
      >
        <div>
          <h2 id="wheel-snapshots-heading" className="font-display text-lg text-zinc-900">
            Integrated Life Audit snapshots over time
          </h2>
          <p className="mt-1 text-sm text-zinc-500">
            Intakes, quarterly audits, monthly seeds, and retakes in order. Use the eye to preview on
            the mini chart; click a row for every question and score.
          </p>
        </div>
        {inner}
      </section>
    )
  }

  return inner
}
